import React from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Card as RawCard } from "@/components/ui/card";
import {
  ArrowRight,
  Clock,
  Ship,
  Calendar as CalendarIcon,
} from "lucide-react";
import { motion } from "framer-motion";
import { format } from "date-fns";

/**
 * @param {{
 *   ferry: {
 *     duration_minutes: number;
 *     image: string;
 *     operator: string;
 *     vessel_type: string;
 *     origin_port: string;
 *     destination_port: string;
 *     base_price_per_passenger: number;
 *   };
 *   onSelect: () => void;
 *   index: number;
 *   selectedDate?: Date;
 *   returnDate?: Date;
 *   passengerCount?: number;
 *   isRoundTrip?: boolean;
 * }} props
 */
export default function FerryCard({
  ferry,
  onSelect,
  index,
  selectedDate,
  returnDate,
  passengerCount,
  isRoundTrip,
}) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Card = /** @type {React.ComponentType<any>} */ (RawCard);
  const formattedDuration = `${Math.floor(ferry.duration_minutes / 60)}h ${ferry.duration_minutes % 60}m`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
    >
      <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 group cursor-pointer border-gray-200 bg-white rounded-2xl">
        <div className="relative h-48 overflow-hidden bg-gradient-to-br from-blue-400 to-blue-600">
          <img
            src={ferry.image}
            alt={`${ferry.operator} ferry`}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute top-3 right-3">
            <div className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-[#0B2421]">
              {ferry.vessel_type}
            </div>
          </div>
          {isRoundTrip && (
            <div className="absolute top-3 left-3">
              <div className="bg-orange-500/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-white">
                Round-Trip
              </div>
            </div>
          )}
          <div className="absolute bottom-3 left-3 right-3">
            <div className="flex items-center gap-2 text-white">
              <Ship className="h-5 w-5" />
              <span className="text-sm font-medium">{ferry.operator}</span>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4 bg-white">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1">
                <div className="text-sm font-semibold text-[#0B2421]">{ferry.origin_port}</div>
              </div>
              <ArrowRight className="h-5 w-5 text-[#0B2421] mx-2 flex-shrink-0" />
              <div className="flex-1 text-right">
                <div className="text-sm font-semibold text-[#0B2421]">
                  {ferry.destination_port}
                </div>
              </div>
            </div>

            {selectedDate && (
              <div className="mb-3 p-2 bg-white rounded-lg">
                <div className="flex items-center gap-1 text-gray-700 text-sm">
                  <CalendarIcon className="h-4 w-4" />
                  <span className="font-medium">
                    {format(selectedDate, "dd MMM yyyy")}
                  </span>
                  {isRoundTrip && returnDate && (
                    <>
                      <ArrowRight className="h-3 w-3 mx-1" />
                      <span className="font-medium">
                        {format(returnDate, "dd MMM yyyy")}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 text-gray-600">
                <Clock className="h-4 w-4" />
                <span className="text-sm">{formattedDuration}</span>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500">From</div>
                <div className="text-xl font-bold text-[#D4AF37]">
                  SGD {ferry.base_price_per_passenger}
                  {isRoundTrip && (
                    <span className="text-sm text-[#D4AF37]"> × 2</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Button
            onClick={onSelect}
            className="w-full bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold rounded-xl"
          >
            View Schedules
          </Button>
        </div>
      </Card>
    </motion.div>
  );
}
